import { DesignInteractionEnum, DesignSourceEnum } from '@insights/insights-data-provider';
import { ComponentIds } from '@inter-ikea-core/core';
import {
  HelpLinesComponentEditMode,
  HelpLinesComponentShape,
  HelpLinesComponentVisible,
  HistoryEventSystem,
  IncludedProductComponent,
  LegacyRangeIds,
  LoadVPCResponse,
  ShapeTypeEnum,
  VPCConfiguration,
  WallShape
} from '@inter-ikea-storageone/range';
import { RemoveDoorsAndLightsOnceInterceptor } from '@inter-ikea-storageone/range-pax';
import { getVpcSource, parseVpcSource } from 'analytics-api/insights/utils';
import { reportInitPlanningSession, reportVpcInteraction } from 'analytics/reporters';
import { setShape } from 'ducks/helpLines';
import { useAppDispatch } from 'ducks/hooks';
import { hideLoadingScreen, setConfigurationPossible } from 'ducks/layout';
import { getProductsNotInMarket, selectGroup } from 'ducks/products';
import { setUnsavedChangesSheetDimissed, showResumeLastSessionModal } from 'ducks/sheets';
import { getVpcCode, invalidateVpcCode, saveVpcCode, saveVpcImageUrl } from 'ducks/vpc';
import * as R from 'ramda';
import React, { useContext, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { AppRoutes } from 'routes';
import { tryParse } from 'utils/dataUtils';
import { isKiosk, isMobile } from 'utils/platform';
import { e2e, getQuery, getRange } from 'utils/queryUtils';
import { useCore } from './core';
import { useInsights } from './insights';
import { useScreenshot } from './screenshot';
import { useServiceSettings } from './serviceSettings';
import { SaveService } from '@inter-ikea-kompis/services';
import { resetDesignId } from 'ducks/designId';
import { isSpaceEnabled } from 'utils/integration';

type VpcLoadResult = {
  response: LoadVPCResponse;
  supported: boolean;
  legacy: boolean;
  init: () => Promise<Omit<VpcLoadResult, 'init'> & { initialized: boolean }>;
};

type DesignIdLoadResult = {
  init: () => void;
};

type VpcLoadOptions = {
  sprCode?: string;
  silent?: boolean;
};

const range = getRange();

// Do not export these unless actually necessary. Use the hook
const STORAGE_ONE_PERSISTED_SCENE = `storageone-scene-persist-${range}`;
const STORAGE_ONE_PERSISTED_VPC_CODE = `storageone-scene-persist-vpc-${range}`;
const STORAGE_ONE_PERSISTED_WALL_SHAPE = `storageone-scene-persist.wall-shape-${range}`;

type SceneHandler = {
  hasPersistedScene: () => boolean;
  loadLocalScene: () => void;
  loadVpcScene: (
    vpcCode: string,
    vpcSource: DesignSourceEnum,
    options?: VpcLoadOptions
  ) => Promise<VpcLoadResult | null>;
  clearLocalScene: () => void;
  setLocalScene: () => void;
  setLocalWallShape: (wallShape: WallShape) => void;
  clearLocalWallShape: () => void;
  loadDesignIdScene: (
    designId: string,
    designSourceEnum: DesignSourceEnum,
    saveService: SaveService,
    token?: string | undefined
  ) => Promise<DesignIdLoadResult | null>;
};

export const SceneHandlerContext = React.createContext<SceneHandler>({
  hasPersistedScene: () => false,
  loadLocalScene: () => {},
  loadVpcScene: async () => null,
  clearLocalScene: () => {},
  setLocalWallShape: () => {},
  clearLocalWallShape: () => {},
  setLocalScene: () => {},
  loadDesignIdScene: async () => null
});

export const useSceneHandler = () => useContext(SceneHandlerContext);

type Props = {
  children: React.ReactNode;
};

export const SceneHandlerProvider = ({ children }: Props) => {
  const coreApi = useCore();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const insights = useInsights();
  const {
    kompisSettings,
    dexfEndpoints: { itemList }
  } = useServiceSettings();
  const { resetImages, setImage } = useScreenshot();
  const invalidProducts = useSelector(getProductsNotInMarket);
  const vpcCode = useSelector(getVpcCode);
  const didRunEffect = useRef(false);
  const prevPathRef = useRef(location.pathname);

  const checkSceneEmpty = () => coreApi.entities.getAll?.().length === 0;

  // Report init planning session if DPC doesn't have a planning ID
  const reportInitIfNoPlanningId = (...args: Parameters<typeof reportInitPlanningSession>) => {
    const planningId = insights.getIds()?.planningId;
    if (!planningId) reportInitPlanningSession(...args);
  };

  const loadLocalVpcCode = () => {
    const localVpc = localStorage.getItem(STORAGE_ONE_PERSISTED_VPC_CODE);
    localVpc && dispatch(saveVpcCode(localVpc));
  };

  const setLocalVpcCode = () => {
    if (vpcCode) {
      localStorage.setItem(STORAGE_ONE_PERSISTED_VPC_CODE, vpcCode);
    } else if (didRunEffect.current && location.pathname === AppRoutes.PLANNER) {
      localStorage.removeItem(STORAGE_ONE_PERSISTED_VPC_CODE);
    }
  };

  useEffect(() => {
    setLocalVpcCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vpcCode]);

  /*
   * Initialize a configuration into the scene.
   * This is intended for initializing configurations
   * loaded from VPC, designId or localStorage
   */
  const initializeConfiguration = async (
    configuration: VPCConfiguration,
    setRoom: boolean,
    identifier?: string
  ) => {
    let success = false;

    try {
      // Do not trigger history events when loading
      coreApi.getSystem<HistoryEventSystem>('HistoryEventSystem')?.disable();
      coreApi.camera.resetCamera?.();
      await coreApi.loadScene(configuration, setRoom);

      const invalidProductsInScene = R.intersection(
        coreApi.entities
          .getByComponent(ComponentIds.PRODUCT_COMPONENT_ID)
          .filter(e => !e.hasComponent(IncludedProductComponent.id))
          .map(coreApi.entities.getProductRef),
        invalidProducts.map(p => p.coreProductId)
      );

      if (invalidProductsInScene.length) {
        clearLocalScene();
        identifier && coreApi.emit.onLoadVpcFailure(identifier);
        throw Error(`Invalid products ${invalidProductsInScene}`);
      }

      if (configuration.kvadratVpc) coreApi.kvadratRoom?.loadBlueprint(configuration.kvadratVpc);

      coreApi.camera.focusSolutionAfterLoad?.(isMobile(), !e2e());
      dispatch(selectGroup(''));
      success = true;
    } catch (error) {
      coreApi.reportErrorToSentry(
        new Error(
          `Failed to initialize configuration: ${identifier ?? 'unknown'}, ${error.message}`
        )
      );
      // eslint-disable-next-line no-console
      console.error(
        `Failed to initialize configuration: ${identifier ?? 'unknown'}, ${error.message}`
      );
      success = false;
    } finally {
      coreApi.getSystem<HistoryEventSystem>('HistoryEventSystem')?.enable();
    }

    return success;
  };

  /**
   * Fetch scene data from localStorage and load into scene
   */
  const loadLocalScene = async () => {
    if (isKiosk()) return false;

    const sceneData = localStorage.getItem(STORAGE_ONE_PERSISTED_SCENE);
    const wallData = localStorage.getItem(STORAGE_ONE_PERSISTED_WALL_SHAPE);

    if (!sceneData) return;

    const parsed = JSON.parse(sceneData);
    const wall = tryParse<WallShape>(wallData, ({ type, dimensions }) => !!type && !!dimensions);

    dispatch(setUnsavedChangesSheetDimissed(true));

    if (!parsed.scene) {
      localStorage.removeItem(STORAGE_ONE_PERSISTED_SCENE);
      return false;
    }

    coreApi.kvadratRoom?.kvadrat?.setBlueprint(parsed.blueprint);
    if (!initializeConfiguration(parsed.scene, false)) return;
    dispatch(selectGroup(''));

    reportInitIfNoPlanningId({
      vpcCode: '',
      source: DesignSourceEnum.resumeLastDesign
    });

    if (wall) {
      dispatch(setShape(wall.type));
      const helpLinesVisible = wall.type !== ShapeTypeEnum.off;
      const scene = coreApi.getServices().sceneManager.scene;
      scene?.addComponent(new HelpLinesComponentVisible(helpLinesVisible));
      scene?.addComponent(new HelpLinesComponentShape(wall.type, wall.dimensions));
      scene?.addComponent(new HelpLinesComponentEditMode(false));
    } else {
      clearLocalWallShape();
    }

    loadLocalVpcCode();

    return true;
  };

  const fetchVpcScene = async (vpcCode: string) => {
    const response = await coreApi.loadVpc(
      vpcCode,
      kompisSettings,
      range,
      kompisSettings.dexfApiKey,
      itemList
    );

    let meta = null;

    const validateRange = (application: string) => {
      const appId = kompisSettings.dexfApplicationId.toUpperCase();

      return (
        application in LegacyRangeIds ||
        application === appId ||
        (application === 'STORAGEONEEKET' && appId === 'STORAGEONEBESTA')
      );
    };

    if (response) {
      meta = {
        supported: validateRange(response.application),
        legacy: response.application in LegacyRangeIds,
        response
      };

      reportVpcInteraction({
        vpcCode: response.configurationId,
        interaction: DesignInteractionEnum.getConfiguration
      });
    }

    return meta;
  };

  /**
   *
   * Fetches DesignId configuration and loading function
   */
  const loadDesignIdScene = async (
    designId: string,
    designSourceEnum: DesignSourceEnum,
    saveService: SaveService,
    token?: string | undefined
  ) => {
    const result = await saveService.getConfiguration({ token: token, id: designId });

    if (!result) return null;

    // Reset solution data
    dispatch(invalidateVpcCode());
    dispatch(resetDesignId());
    clearLocalScene();
    resetImages();

    const init = async () =>
      (await initializeConfiguration(result.configuration as VPCConfiguration, false, designId)) &&
      setLocalScene();

    // Currently init planning should be reported on successful fetch
    reportInitPlanningSession({
      vpcCode: designId,
      source: designSourceEnum
    });

    return { init };
  };

  /**
   *
   * Fetches VPC config, exposes metadata and loading function
   */
  const loadVpcScene = async (
    vpcCode: string,
    vpcSource: DesignSourceEnum,
    options: VpcLoadOptions = {}
  ) => {
    const { fromGuide, fromGallery, fromSpr } = parseVpcSource(vpcSource);

    const result = await fetchVpcScene(vpcCode);

    if (!result) return null;

    const { response, legacy, supported } = result;

    // Reset solution data
    dispatch(invalidateVpcCode());
    dispatch(resetDesignId());
    clearLocalScene();
    resetImages();

    if (fromGuide) {
      // Guide VPCs are transformed on load (lights and doors removed)
      coreApi
        .getMainProductCatalog()
        .addInterceptor(new RemoveDoorsAndLightsOnceInterceptor(coreApi.getMainProductCatalog()));

      // Don't save VPC data to state as we've transformed it on load
      options.silent = true;
    }

    if (response.image) setImage(response.image);
    if (response.imageUrl?.length) dispatch(saveVpcImageUrl(response.imageUrl[0].url));

    if (!options.silent) {
      dispatch(saveVpcCode(vpcCode));
      dispatch(setConfigurationPossible(!legacy));
    }

    const init = async () => {
      const res = { ...result, initialized: false };

      if (
        !supported ||
        legacy ||
        !(await initializeConfiguration(response.configuration, true, vpcCode))
      ) {
        return res;
      }

      if (fromGuide || fromGallery || fromSpr) {
        // Solutions in gallery, SPR and guide are built in specific markets so they need to be resaved in the
        // current market for the product list to be correct
        dispatch(invalidateVpcCode());
        dispatch(resetDesignId());
      }

      setLocalScene();
      if (result.response.configuration.wallShape) {
        setLocalWallShape(result.response.configuration.wallShape);
      }
      res.initialized = true;
      return res;
    };

    // Currently init planning should be reported on successful fetch
    reportInitPlanningSession({
      vpcCode,
      source: vpcSource,
      spr: options.sprCode,
      payload: fromGuide ? { guide_context: 'guida' } : undefined
    });

    return { ...result, init };
  };

  /**
   * Clear all scene data saved in localStorage and reset scene
   */
  const clearLocalScene = () => {
    localStorage.removeItem(STORAGE_ONE_PERSISTED_SCENE);
    localStorage.removeItem(STORAGE_ONE_PERSISTED_VPC_CODE);
    coreApi.resetScene();
    clearLocalWallShape();
  };

  /**
   * Save current scene data to localStorage
   */
  const setLocalScene = () => {
    if (isKiosk()) return;

    if (!checkSceneEmpty()) {
      const scene = coreApi.saveScene();
      const blueprint = coreApi.kvadratRoom?.kvadrat?.getBlueprint();

      const sceneData = {
        scene: JSON.parse(scene),
        blueprint
      };

      const stringified = JSON.stringify(sceneData);

      localStorage.setItem(STORAGE_ONE_PERSISTED_SCENE, stringified);
    } else {
      !!localStorage.getItem(STORAGE_ONE_PERSISTED_SCENE) &&
        localStorage.removeItem(STORAGE_ONE_PERSISTED_SCENE);
    }
  };

  /**
   * Clear wall shape data saved in localStorage
   */
  const clearLocalWallShape = () => {
    localStorage.removeItem(STORAGE_ONE_PERSISTED_WALL_SHAPE);
  };

  /**
   * Save wall shape data to localStorage
   */
  const setLocalWallShape = (wallShape: WallShape) => {
    localStorage.setItem(STORAGE_ONE_PERSISTED_WALL_SHAPE, JSON.stringify(wallShape));
  };

  const hasPersistedScene = () => !isKiosk() && !!localStorage.getItem(STORAGE_ONE_PERSISTED_SCENE);

  useEffect(() => {
    // THis needs to be done to make sure that the effect is run only once per location change
    // Previously the effect was run multiple times when the location changed with different states causing the final
    // state scene empty check to always run, making it hard to focus the camera on the room
    if (prevPathRef.current !== location.pathname) {
      didRunEffect.current = false;
      prevPathRef.current = location.pathname;
    }
    if (didRunEffect.current) return;
    // Automatic scene loading in this effect - either from vpc or localStorage
    if (location.pathname !== AppRoutes.PLANNER && location.pathname !== AppRoutes.SUMMARY) return;

    // Combining query params before and in the hash path.
    const { sourceValue, validSource } = getVpcSource(
      getQuery(location.search.concat(window.location.search.replace('?', '&')))
    );

    const query = getQuery(location.search);
    const vpcCode = query.get('vpc') ?? undefined;

    if (vpcCode) {
      // Handle VPC code in query param
      const source = validSource ? sourceValue : DesignSourceEnum.clipboard;

      loadVpcScene(vpcCode, source)
        .then(scene => scene?.init())
        .then(scene => {
          history.replace(scene?.legacy ? AppRoutes.SUMMARY : AppRoutes.PLANNER);
        });
    } else if (checkSceneEmpty()) {
      // Handle an empty scene
      if (hasPersistedScene()) {
        if (history.action === 'POP') {
          loadLocalScene();
        } else {
          dispatch(showResumeLastSessionModal());
        }
      } else {
        coreApi.camera.focusSolution?.(undefined, undefined, isMobile(), !e2e());

        const source = isSpaceEnabled()
          ? DesignSourceEnum.space
          : DesignSourceEnum.startFromScratch;
        // Start from scratch
        reportInitIfNoPlanningId({
          vpcCode: '',
          source: source
        });
      }
    }

    didRunEffect.current = true; // mark effect as executed for this location

    // Hide loading screen when viewer is ready
    coreApi.ipexViewer.on_current_scene_ready?.().then(() => dispatch(hideLoadingScreen()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, coreApi]);

  return (
    <SceneHandlerContext.Provider
      value={{
        hasPersistedScene,
        loadLocalScene,
        loadVpcScene,
        clearLocalScene,
        setLocalScene,
        clearLocalWallShape,
        setLocalWallShape,
        loadDesignIdScene
      }}
    >
      {children}
    </SceneHandlerContext.Provider>
  );
};
